import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { api, ErroApi, type Usuario } from '../api'
import { FALHA_GENERICA, mensagemDeErro } from './mensagemLogin'

/**
 * A tela de login — a primeira coisa que qualquer pessoa vê, inclusive o telão
 * da sala. **Não existe tela pública neste painel**: ele mostra meta e venda
 * por marca, mais pedido individual.
 *
 * O fundo camuflado e os filtros SVG já são montados pelo `Aplicacao.tsx`;
 * montá-los aqui duplicaria o SVG na página.
 *
 * Nada de nome ou senha vai para `localStorage`. A sessão é o cookie `HttpOnly`
 * que o `POST /api/login` grava, e guardar credencial em disco só acrescentaria
 * um lugar para ela vazar — num computador de sala, compartilhado.
 */
export function Login({ aoEntrar }: { aoEntrar: (usuario: Usuario) => void }) {
  const [nome, setNome] = useState('')
  const [senha, setSenha] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  /* Conta as recusas. Serve a duas coisas que precisam acontecer A CADA falha,
     inclusive quando a falha repete o mesmo texto: devolver o foco ao campo de
     senha e remontar o `role="alert"` para o leitor de tela anunciar de novo.
     Com `erro` sozinho isso não funciona — `setErro` com o mesmo texto não
     provoca repintura, e a segunda tentativa errada ficaria muda. */
  const [falhas, setFalhas] = useState(0)
  const campoSenha = useRef<HTMLInputElement>(null)

  const vazio = nome.trim() === '' || senha === ''

  /* Foco e seleção vivem AQUI, e não no `catch`: lá o input ainda está
     `disabled` no DOM (o React repinta depois) e `select()` não devolve o foco
     por si — desabilitar o campo em foco joga o foco para o `body`. Sem este
     efeito a senha fica visualmente lá e o teclado da sala digita no nada. */
  useEffect(() => {
    if (falhas === 0) return   // no primeiro render o foco é do campo de usuário
    campoSenha.current?.focus()
    campoSenha.current?.select()
  }, [falhas])

  /* Todo caminho de recusa termina aqui: libera o formulário, mostra o texto e
     conta a falha. Esquecer o `setEnviando(false)` em UM desses caminhos deixa
     a tela presa em "Entrando…" com os dois campos desabilitados, e aí só o F5
     destrava — num telão de sala, isso é a tela morta. */
  function recusar(texto: string) {
    setErro(texto)
    setEnviando(false)
    /* A senha continua no campo, e o efeito acima a seleciona: erro de login
       quase sempre é um caractere errado, e apagar o campo obrigaria a
       redigitar tudo. */
    setFalhas((quantas) => quantas + 1)
  }

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    /* Segunda barreira contra o duplo clique: o botão desabilitado cobre o
       mouse, este `if` cobre o Enter repetido antes do React repintar. O scrypt
       do servidor custa ~80 ms, então a janela existe de verdade. */
    if (enviando || vazio) return

    setEnviando(true)
    setErro(null)

    let autenticado: Usuario | undefined
    try {
      const resposta = await api.entrar(nome.trim(), senha)
      autenticado = resposta?.usuario
    } catch (falha) {
      const status = falha instanceof ErroApi ? falha.status : null
      recusar(mensagemDeErro(status, falha instanceof Error ? falha.message : ''))
      return
    }

    /* 200 sem `usuario` no corpo (proxy que intercepta a rota, redirecionamento
       de SSO respondendo JSON de outra coisa) NÃO pode virar
       `aoEntrar(undefined)`: a tela voltaria ao login presa em "Entrando…". */
    if (!autenticado) {
      recusar(FALHA_GENERICA)
      return
    }

    /* Fora do `try` de propósito: neste ponto o cookie de sessão JÁ está
       gravado. Chamar `aoEntrar` dentro do `try` faria uma falha dele virar
       "não foi possível entrar" depois de um login que deu certo — e a tela
       mentiria sobre o estado do servidor. `aoEntrar` desmonta esta tela, então
       não há estado para arrumar depois. */
    aoEntrar(autenticado)
  }

  return (
    <div className="login">
      <form className="caixa-login" onSubmit={enviar}>
        <div>
          <div className="titulo-evento">War Room</div>
          <div className="subtitulo-evento">Time Comercial Lunelli · Meta 2026</div>
        </div>

        {/* Sem `required` nos campos, de propósito: `input:invalid` no tema.css
            pinta a borda de vermelho, e com `required` a tela nasceria vermelha
            antes de alguém digitar. O botão desabilitado faz o mesmo trabalho,
            e de quebra evita um 401 que conta tentativa no travamento da conta. */}
        <div className="campo">
          <label htmlFor="login-nome">Usuário</label>
          <input
            id="login-nome"
            name="nome"
            type="text"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={120}
            autoFocus
            disabled={enviando}
          />
        </div>

        <div className="campo">
          <label htmlFor="login-senha">Senha</label>
          <input
            id="login-senha"
            name="senha"
            type="password"
            ref={campoSenha}
            value={senha}
            onChange={(evento) => setSenha(evento.target.value)}
            autoComplete="current-password"
            maxLength={512}
            disabled={enviando}
          />
        </div>

        {erro === null ? null : (
          /* `role="alert"` para o leitor de tela anunciar sem precisar de foco.
             O `key` pela contagem de falhas remonta o parágrafo: anúncio de
             `alert` só sai quando o nó entra ou muda, e dois 401 seguidos têm o
             mesmo texto. */
          <p className="erro" role="alert" key={falhas}>{erro}</p>
        )}

        <button type="submit" className="botao primario" disabled={enviando || vazio}>
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </div>
  )
}
